import React, { useState, useEffect } from "react";
import { Toaster } from "react-hot-toast";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import Auth from "./components/Auth";
import Dashboard from "./components/Dashboard";
import NotFound from "./components/NotFound";
import EmbedChat from "./components/EmbedChat";
import Chat from "./components/Chat";
import PrivacyPolicy from "./components/PrivacyPolicy";
import TermsOfService from "./components/TermsOfService";
import Home from "./pages/Home";
import Pricing from "./pages/Pricing";
import HowToUse from "./pages/HowToUse";
import NavBar from "./components/NavBar";
import Billing from "./components/Billing";
import VerifySuccess from "./components/VerifySuccess";
import Footer from "./components/Footer";
import "./App.css";
import { secureStorage } from "./SecureStorage";
import EmbedChatRoute from "./EmbedChatRoute";

// Protected route wrapper
const ProtectedRoute = ({ element, org, requireActiveSub = false }) => {
  if (!org) return <Navigate to="/auth/login" replace />;
  if (requireActiveSub && org.subscription_status !== "active") {
    return <Navigate to="/billing" replace />;
  }
  return element;
};

function App() {
  const [orgId, setOrgId] = useState(() => {
    const token = secureStorage.getItem("auth_token");
    if (!token) return null;

    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload.org_id || null;
    } catch {
      return null;
    }
  });
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "auth_token" && event.newValue === null) {
        secureStorage.removeItem("auth_token");
        setOrgId(null);
        setOrganization(null);
        navigate("/auth/login");
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);


  function SubdomainRedirect() {
    const location = useLocation();
  
    useEffect(() => {
      const host = window.location.hostname;
  
      if (host === "querybox.app" || host === "www.querybox.app") return;
  
      const match = host.match(/^([^.]+)\.querybox\.app$/);
      if (!match) return;
  
      const subdomain = match[1];
      if (!subdomain || subdomain === "www" || subdomain === "chat") return;
      // if (!subdomain || subdomain === "www") return;
  
      const currentPath = location.pathname;
      const expectedChatPath = `/chat/${subdomain}`;
  
      // Only fetch org status for subdomains
      fetch(`${process.env.REACT_APP_API_BASE_URL}/org-status/${subdomain}`)
        .then((res) => res.json())
        .then((data) => {
          if (data.status !== "active") {
            // Inactive org → go to main billing
            window.location.replace(`${process.env.REACT_APP_API_BASE_URL}`);
            return;
          }
  
          // === NEW LOGIC ===
          // 1️⃣ Root of subdomain → go to chat
          if (currentPath === "/") {
            window.location.replace(`https://${host}${expectedChatPath}`);
            return;
          }
  
          // 2️⃣ Already on correct chat → do nothing
          if (currentPath === expectedChatPath) return;
  
          // 3️⃣ Routes that should always go to main site
          const mainSiteRoutes = [
            "/pricing",
            "/how-to-use",
            "/auth/login",
            "/auth/signup",
            "/dashboard",
            "/billing",
            "/verify-success",
          ];
  
          if (mainSiteRoutes.some(route => currentPath.startsWith(route))) {
            window.location.replace(`https://querybox.app${currentPath}`);
            return;
          }
  
          // 4️⃣ Any wrong chat path → fix
          if (currentPath.startsWith("/chat/") && currentPath !== expectedChatPath) {
            window.location.replace(`https://${host}${expectedChatPath}`);
            return;
          }
  
          // 5️⃣ Anything else → leave on subdomain
        })
        .catch(() => {
          // Network/other errors → fallback to main site
          window.location.replace("https://querybox.app");
        });
    }, [location.pathname]);
  
    return null;
  }

  const isEmbed = window.location.pathname.startsWith("/embed");
  const [selectedColor, setSelectedColor] = useState("#4a3aff"); // default widget color


  // useEffect(() => {
  //   if (!orgId) return;
  
  //   // inject widget script dynamically
  //   const script = document.createElement("script");
  //   // script.src = "http://localhost:3000/widget.js";
  //   script.src = "https://www.querybox.app/widget.js"; // production widget
  //   script.async = true;
  
  //   // Set organization ID
  //   script.setAttribute("data-org", 6);
  
  //   // Optionally set widget color
  //   if (selectedColor) {
  //     script.setAttribute("data-color", "#53949d");
  //   }
  
  //   // Append to body
  //   document.body.appendChild(script);
  
  //   // Cleanup on unmount
  //   return () => {
  //     document.body.removeChild(script);
  //   };
  // }, [orgId, selectedColor]);
  


  
  // Fetch organization info securely using orgId
  useEffect(() => {
    const fetchOrg = async () => {
      setLoading(true);
      if (!orgId) {
        setOrganization(null);
        setLoading(false);
        return;
      }

      try {
        const token = secureStorage.getItem("auth_token");
        if (!token) {
          console.error("No auth token found!");
          return;
        }
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/company/bio`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: token ? `Bearer ${token}` : "",
          },
        });

        if (!res.ok) throw new Error("Failed to fetch org");
        const data = await res.json();

        const orgData = {
          company_name: data.company_name,
          company_handle: data.company_handle,
          company_email: data.company_email,
          company_id: data.company_id,
          subscription_status: data.subscription_status || "inactive",
        };
        setOrganization(orgData);

        // Auto-redirect after login
        if (window.location.pathname === "/auth/login") {
          if (orgData.subscription_status === "active") {
            navigate("/dashboard", { replace: true });
          } else {
            navigate("/billing", { replace: true });
          }
        }
      } catch (err) {
        console.error("Failed to fetch organization:", err);
        setOrganization(null);
      } finally {
        setLoading(false);
      }
    };

    fetchOrg();
  }, [orgId, navigate]);

  // Show loader ONLY on dashboard route
  if (loading && location.pathname === "/dashboard") {
    return (
      <div id="appLoadingModal" className="modal-index-overlay">
        <div className="modal-index-content">
          <div className="dot-loader">
            <span></span>
            <span></span>
            <span></span>
          </div>
          <p id="app-modal-message">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  

  return (
    <div className="App">
       {!isEmbed && (
        <NavBar
          orgId={orgId}
          onLogout={() => {
            secureStorage.removeItem("auth_token");
            setOrgId(null);
            setOrganization(null);
            navigate("/auth/login");
          }}
        />
      )}
  
      {/* 👇 Subdomain handler */}
      <SubdomainRedirect />
  
      {/* 👇 Main page content */}
      <div className="page-content">
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<Home />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/how-to-use" element={<HowToUse />} />
          <Route
            path="/auth/login"
            element={<Auth onLogin={setOrgId} type="login" />}
          />
          <Route
            path="/auth/signup"
            element={<Auth onLogin={setOrgId} type="signup" />}
          />
          <Route path="/verify-success" element={<VerifySuccess />} />
          
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms" element={<TermsOfService />} />
          
          {/* ⚡ Dedicated EmbedChat route - NO NavBar/ Footer */}
          <Route path="/embed" element={<EmbedChatRoute />} />

          {/* Premium protected route */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute
                org={organization}
                element={<Dashboard orgId={orgId} />}
                requireActiveSub={true}
              />
            }
          />
  
          {/* Billing always accessible */}
          <Route
            path="/billing"
            element={<Billing organization={organization} />}
          />
  
          <Route path="/chat/:handle" element={<Chat />} />

          {/* ⚠️ Catch-all Not Found route */}
          <Route path="*" element={<NotFound />} />
        </Routes>
        <Toaster position="top-right" reverseOrder={false} />
      </div>
  
      {/* 👇 Footer shows at bottom after scroll */}
      {/* <Footer /> */}
      {!isEmbed && <Footer />}
    </div>
  );
  

}

export default function RootApp() {
  return (
    <Router>
      <App />
    </Router>
  );
}
